import { z } from 'zod'

type ExtendResult = { ok: true; schema: z.core.$ZodType } | { ok: false }

type ExtendObject<
  ObjectValue extends object,
  Extension extends z.ZodRawShape,
  OutputOrInput extends 'output' | 'input'
> = {
  [Key in keyof ObjectValue]: Key extends keyof Extension
    ? OutputOrInput extends 'output'
      ? z.output<Extension[Key]>
      : z.input<Extension[Key]>
    : ObjectValue[Key]
}

type ExtendValue<
  Value,
  Path extends readonly string[],
  Extension extends z.ZodRawShape,
  OutputOrInput extends 'output' | 'input'
> = Value extends (infer Item)[]
  ? ExtendValue<Item, Path, Extension, OutputOrInput>[]
  : Path extends readonly []
    ? Value extends object
      ? ExtendObject<Value, Extension, OutputOrInput>
      : Value
    : Path extends readonly [infer Key extends string, ...infer Rest extends readonly string[]]
      ? Value extends object
        ? Key extends keyof Value
          ? {
              [Property in keyof Value]: Property extends Key
                ? ExtendValue<Value[Property], Rest, Extension, OutputOrInput>
                : Value[Property]
            }
          : Value
        : Value
      : Value

type ExtendedSchema<
  Schema extends z.ZodType,
  Path extends readonly string[],
  Extension extends z.ZodRawShape
> = z.ZodType<
  ExtendValue<z.output<Schema>, Path, Extension, 'output'>,
  ExtendValue<z.input<Schema>, Path, Extension, 'input'>
>

/**
 * Walks `path` through a generated schema's object/array/optional/nullable/union wrappers
 * and, once the addressed object is reached, applies `.extend(extension)` to it. Union
 * branches that do not contain `path` pass through unchanged, so one call can target a field
 * that only exists on some variants of a discriminated response union.
 */
const tryExtendField = (
  schema: z.core.$ZodType,
  path: readonly string[],
  extension: z.ZodRawShape
): ExtendResult => {
  if (schema instanceof z.ZodOptional) {
    const result = tryExtendField(schema.unwrap(), path, extension)
    return result.ok ? { ok: true, schema: z.optional(result.schema) } : result
  }
  if (schema instanceof z.ZodNullable) {
    const result = tryExtendField(schema.unwrap(), path, extension)
    return result.ok ? { ok: true, schema: z.nullable(result.schema) } : result
  }
  if (schema instanceof z.ZodArray) {
    const result = tryExtendField(schema.unwrap(), path, extension)
    return result.ok ? { ok: true, schema: z.array(result.schema) } : result
  }
  if (schema instanceof z.ZodUnion) {
    const options = schema.options.map((option) => ({
      option,
      result: tryExtendField(option, path, extension)
    }))
    if (!options.some(({ result }) => result.ok)) return { ok: false }
    return {
      ok: true,
      schema: schema.clone({
        ...schema.def,
        options: options.map(({ option, result }) => (result.ok ? result.schema : option))
      })
    }
  }
  if (path.length === 0) {
    if (!(schema instanceof z.ZodObject)) return { ok: false }

    // Only keys this branch already declares are extended, so union siblings never gain fields.
    const existingKeys = new Set(Object.keys(schema.def.shape))
    const applicableExtension = Object.fromEntries(
      Object.entries(extension).filter(([key]) => existingKeys.has(key))
    )
    if (Object.keys(applicableExtension).length === 0) return { ok: false }

    return { ok: true, schema: schema.extend(applicableExtension) }
  }
  if (!(schema instanceof z.ZodObject)) return { ok: false }

  const key = path[0]
  if (key === undefined) return { ok: false }
  const shape: Record<string, z.core.$ZodType | undefined> = schema.def.shape
  const field = shape[key]
  if (field === undefined) return { ok: false }

  const result = tryExtendField(field, path.slice(1), extension)
  return result.ok ? { ok: true, schema: schema.extend({ [key]: result.schema }) } : { ok: false }
}

/**
 * Extends a generated contract schema at a nested field path with additional Zod validation,
 * without hand-declaring a duplicate of the shape it walks through. Transparently unwraps
 * `.optional()`/`.nullable()`/arrays, and tolerates union branches that lack the path.
 * Throws if `path` does not resolve anywhere on `schema` (a sign the generated contract drifted).
 */
export const extendSchemaField = <
  Schema extends z.ZodType,
  const Path extends readonly string[],
  Extension extends z.ZodRawShape
>(
  schema: Schema,
  path: Path,
  extension: Extension
): ExtendedSchema<Schema, Path, Extension> => {
  const result = tryExtendField(schema, path, extension)
  if (!result.ok) {
    throw new TypeError(`Cannot extend "${path.join('.')}": path not found on schema.`)
  }
  return result.schema as ExtendedSchema<Schema, Path, Extension>
}
